import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { roleName } from '../config/roleNames';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Trophy, 
  Users, 
  Building2, 
  Calendar, 
  LogOut, 
  Plus,
  Settings,
  BarChart3,
  Shield,
  ChevronRight,
  Loader2,
  AlertCircle,
  UserCircle,
  Home,
  ClipboardList,
  DollarSign,
  FileText,
  UserPlus,
  Sun,
  Moon,
  ShoppingCart,
  HandHelping,
  FileWarning,
  CheckCircle2,
  Landmark,
  KeyRound,
  Menu,
} from 'lucide-react';
import FinanceModule from '../olfin/FinanceModule';
import RegistrationCart from '../components/RegistrationCart';
import RSVPPanel from '../components/RSVPPanel';
import VolunteerPanel from '../components/VolunteerPanel';
import DocumentAlerts from '../components/DocumentAlerts';
import PaymentPanel from '../components/PaymentPanel';
import FinancialsPanel from '../components/FinancialsPanel';
// import SchedulePanel from '../components/SchedulePanel';  // superseded by GameSchedulePanel on Schedule tab
import SchedulerPanel from '../components/SchedulerPanel';
import GameSchedulePanel from '../components/GameSchedulePanel';
import OpponentBookingPanel from '../components/OpponentBookingPanel';
import RosterPanel from '../components/RosterPanel';
import ExportPanel from '../components/ExportPanel';
import ReportsPanel from '../components/ReportsPanel';
import NotificationBell from '../components/NotificationBell';
import AdminSettingsPanel from '../components/AdminSettingsPanel';
import HqSettingsPanel from '../components/HqSettingsPanel';
import SponsorsMediaPanel from '../components/SponsorsMediaPanel';
import MorningBriefing from '../components/MorningBriefing';
import HeadshotManager from '../components/HeadshotManager';
import CompanyPanel from '../components/CompanyPanel';
import StatsPanel from '../components/StatsPanel';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import ProgramHistoryPanel from '../components/ProgramHistoryPanel';
import CalendarPanel from '../components/CalendarPanel';
import LeagueReportsPanel from '../components/LeagueReportsPanel';
import AvailabilityPanel from '../components/AvailabilityPanel';
import MessagingPanel from '../components/MessagingPanel';
import LineupPanel from '../components/LineupPanel';
import MediaPanel from '../components/MediaPanel';
import TournamentPanel from '../components/TournamentPanel';
import SeasonPanel from '../components/SeasonPanel';
import ActivityLogPanel from '../components/ActivityLogPanel';
import RegistrationHubPanel from '../components/RegistrationHubPanel';
import DraftBoardPanel from '../components/DraftBoardPanel';
import LiveScoringPanel from '../components/LiveScoringPanel';
import GameDayPanel from '../components/GameDayPanel';
import RegistrationFormPanel from '../components/RegistrationFormPanel';
import CohortPanel from '../components/CohortPanel';
import CohortPreviewBanner from '../components/CohortPreviewBanner';
// PublicWebsite import removed Session 19 (May 12 2026) — the Public Site
// top-level tab was deleted; the Website sub-tab inside Tech Settings is a
// different component (WebsiteSettingsSection).
// `StaffPanel` import removed Apr 2026 — top-level Staff tab is gone;
// Org Hub uses `OrgStaffPanel` instead.
import OrganizationsHubPanel from '../components/OrganizationsHubPanel';
import LibraryPanel from '../components/LibraryPanel';
import AdFinancePanel from '../components/AdFinancePanel';
import AdOverviewWidgets from '../components/AdOverviewWidgets';
import PlayerDocumentsSummary from '../components/PlayerDocumentsSummary';
import TeamsHubPanel from '../components/TeamsHubPanel';
import MediaPage from '../components/media/MediaPage';
import ContactsPanel from '../components/contacts/ContactsPanel';
import FamilyDirectoryHubPanel from '../components/FamilyDirectoryHubPanel';
// import ScheduleManager from '../components/ScheduleManager';  // live-WP manager, superseded by GameSchedulePanel (local-first grid) on Schedule tab
import AdminManager from '../components/admin/AdminManager';
import BookingCalendarPanel from '../components/booking/BookingCalendarPanel';
import TechSettingsPanel from '../components/TechSettingsPanel';
import MembersHub from '../components/MembersHub';
import WebsiteVisibilityPanel from '../components/WebsiteVisibilityPanel';
import DownloadManagerPanel from '../components/DownloadManagerPanel';
import IndividualAwardsPanel from '../components/IndividualAwardsPanel';
import ToolsPanel from '../components/ToolsPanel';
import WebsiteContentPanel from '../components/WebsiteContentPanel';
// GameSchedulePanel is rendered inside SchedulerPanel's "Game Schedule" sub-view;
// no direct import needed at Dashboard level.
import SystemMessageBanner from '../components/SystemMessageBanner';
import TrialRibbon from '../components/TrialRibbon';
import HelpdeskWidget from '../components/HelpdeskWidget';
import { ResetCacheButton } from '../components/ResetCacheButton';
import { useNotifications } from '../hooks/useNotifications';
import SchedulerHub from '../components/SchedulerHub';
import CommunicationsHub from '../components/CommunicationsHub';
import ToolsHub from '../components/ToolsHub';

import { NAV_ITEMS, PAGE_META, pageLabel, navItemsFor } from '../config/pageRegistry';
import DashboardSidebar from '../components/DashboardSidebar';
import { useEntitlements } from '../hooks/useEntitlements';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Role codes, the page list, its categories and the catalog payload all live in
// `config/pageRegistry.js` (moved there 2026-10-16). That array is the SOT for
// which pages exist and their ORDER; the backend seeds `ol_page_catalog` from the
// same payload so Semprion can turn a page off for one organization without a
// deploy. Reorder a menu THERE, never in the database.

const ROLE_CONFIG = {
  company_support: {
    label: roleName('company_support'),
    color: 'bg-red-500',
    icon: Shield,
    canCreateLeague: true,
    canCreateOrg: true,
    canManageUsers: true,
    showAllData: true
  },
  league_admin: {
    label: roleName('league_admin'),
    color: 'bg-amber-500',
    icon: Trophy,
    canCreateLeague: false,
    canCreateOrg: true,
    canManageUsers: true,
    showAllData: false
  },
  ad: {
    label: roleName('ad'),
    color: 'bg-blue-500',
    icon: Building2,
    canCreateLeague: false,
    canCreateOrg: false,
    canManageUsers: true,
    showAllData: false
  },
  scheduler: {
    label: roleName('scheduler'),
    color: 'bg-purple-500',
    icon: Calendar,
    canCreateLeague: false,
    canCreateOrg: false,
    canManageUsers: false,
    showAllData: false
  },
  tech_specialist: {
    label: roleName('tech_specialist'),
    color: 'bg-teal-500',
    icon: Settings,
    canCreateLeague: false,
    canCreateOrg: false,
    canManageUsers: false,
    showAllData: false
  },
  treasurer: {
    label: roleName('treasurer'),
    color: 'bg-emerald-500',
    icon: Landmark,
    canCreateLeague: false,
    canCreateOrg: false,
    canManageUsers: false,
    showAllData: false
  },
  coach: {
    label: roleName('coach'),
    color: 'bg-green-500',
    icon: Users,
    canCreateLeague: false,
    canCreateOrg: false,
    canManageUsers: false,
    showAllData: false
  },
  family: {
    label: roleName('family'),
    color: 'bg-cyan-500',
    icon: Home,
    canCreateLeague: false,
    canCreateOrg: false,
    canManageUsers: false,
    showAllData: false
  }
};

export default function Dashboard() {
  const { user, logout, isAdmin, isSemprionAdmin } = useAuth();
  // Subscription entitlements for the org this user is acting in. Fails OPEN:
  // until the map loads (and if it never does) every feature reads as on, so a
  // slow or failed request can never blank an operator's navigation.
  const entitlements = useEntitlements();
  const navigate = useNavigate();
  // Semprion HQ staff default to /admin. Impersonation (auth cookies target a
  // regular user with no semprion_admin role) bypasses this naturally.
  useEffect(() => {
    if (isSemprionAdmin) navigate('/admin', { replace: true });
  }, [isSemprionAdmin, navigate]);
  // OLTRIAL users who just registered but haven't run the org wizard yet
  // should be bounced to /setup-org the moment they land on Dashboard.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`);
        if (data?.pending_org_setup) navigate('/setup-org', { replace: true });
      } catch { /* ignore */ }
    })();
  }, [navigate]);
  const [impersonatingState, setImpersonatingState] = useState(null);
  useEffect(() => {
    // `/auth/me` now carries an `impersonating` block when the current
    // access token has an `act` claim (Phase B silent impersonation).
    // Checking there works for both Semprion and impersonated sessions,
    // unlike `/admin/me` which 403s the moment we're acting-as a regular
    // customer.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`);
        if (!cancelled && data?.impersonating) setImpersonatingState(data.impersonating);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const stopImpersonate = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/impersonate/stop`);
    } catch (e) { /* fall through; logout forces a reset anyway */ }
    window.location.href = '/admin';
  };
  const { theme, toggleTheme, isDark } = useTheme();
  const [leagues, setLeagues] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Lock the Scheduler default tab on the Schedule grid — the user lost this
  // view repeatedly after role-based tab reshuffles, so we pin it and
  // persist the last selection per-user.
  // Menus folded into hubs (AJ 2026-10-14): a remembered old tab opens the hub on that sub-tab.
  const MOVED_TABS = {
    teams_costs: ['teams-hub', 'org-teams'], opponents: ['teams-hub', 'opponents'],
    'draft-board': ['registration-hub', 'draft-board'],
  };
  const _storedTab = (() => { try { return localStorage.getItem('ol.dashboard.activeTab') || ''; } catch { return ''; } })();
  const [teamsHubTab, setTeamsHubTab] = useState(MOVED_TABS[_storedTab]?.[0] === 'teams-hub' ? MOVED_TABS[_storedTab][1] : undefined);
  // Back from Stripe / Square setup (Express signup, own-Stripe OAuth, Square OAuth) → Registration ▸ Payments.
  const _payReturn = (() => { const q = new URLSearchParams(window.location.search); return q.has('express') || q.get('provider') === 'square' || q.get('scope') === 'read_write'; })();
  const [regHubTab, setRegHubTab] = useState(_payReturn ? 'payments' : (MOVED_TABS[_storedTab]?.[0] === 'registration-hub' ? MOVED_TABS[_storedTab][1] : undefined));
  const [activeTab, setActiveTab] = useState(() => (_payReturn ? 'registration-hub' : (MOVED_TABS[_storedTab]?.[0] || _storedTab || 'overview')));
  // AD: Overview = Morning Briefing | Organization; Statistics = Statistics | History
  // (AJ 2026-10-19 — the AD's Platform menu is gone).
  const [overviewTab, setOverviewTab] = useState('briefing');
  const [statsTab, setStatsTab] = useState('stats');
  const [docsTab, setDocsTab] = useState('paperwork');
  const clearTeamsHubTab = useCallback(() => setTeamsHubTab(undefined), []);
  const clearRegHubTab = useCallback(() => setRegHubTab(undefined), []);
  useEffect(() => {
    try { localStorage.setItem('ol.dashboard.activeTab', activeTab); } catch {}
  }, [activeTab]);
  const [scheduleSummary, setScheduleSummary] = useState({ total_slots: 0, pending: 0, confirmed: 0, tbd: 0 });
  
  // Dialog states
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showRegistrationCart, setShowRegistrationCart] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  // Roster tab filters — Session 19 v3 (May 12 2026). AD told us the
  // unfiltered team-button row was duplicating sport tiers; now scoped.
  // Roster filters — data-driven, populated from the canonical entity
  // endpoints (`/api/game-schedule/{org}/sports` and `/api/ol-canonical/seasons/{org}`).
  // Defaults are empty strings until the data lands; the dropdowns then
  // pick the first / current-flagged value.
  const [rosterSeasonFilter, setRosterSeasonFilter] = useState('');
  const [rosterSportFilter, setRosterSportFilter] = useState('');
  const [rosterLevelFilter, setRosterLevelFilter] = useState('');
  // Canonical reference data — sports + seasons for the active org.
  const [sports, setSports] = useState([]);
  const [seasons, setSeasons] = useState([]);
  
  // Form states
  const [leagueForm, setLeagueForm] = useState({ name: '', code: '', divisions: '' });
  const [orgForm, setOrgForm] = useState({ 
    name: '', 
    founding_year: new Date().getFullYear(), 
    league_id: '',
    divisions: '',
    sports: ''
  });

  const primaryRole = user?.active_role?.role_type || user?.roles?.[0]?.role_type || 'family';
  const roleConfig = ROLE_CONFIG[primaryRole] || ROLE_CONFIG.family;
  // OL Finance company scope: the Treasurer's own org (else first visible org).
  const treasurerOrgId = user?.roles?.find(r => r.role_type === 'treasurer')?.org_id || organizations?.[0]?.id;
  // Finance-viewer org scope for the Accountant / AD read-only surfaces.
  const financeOrgId = user?.roles?.find(r => ['treasurer','accountant','ad','aad'].includes(r.role_type))?.org_id || organizations?.[0]?.id;
  // AD Report Viewer nav is gated by the Treasurer's report_viewer grant.
  const [financeViewerGranted, setFinanceViewerGranted] = useState(false);
  useEffect(() => {
    const isAd = (user?.roles || []).some(r => ['ad','aad'].includes(r.role_type));
    if (!isAd || !financeOrgId) { setFinanceViewerGranted(false); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/olfin/report-viewer`, { params: { org_id: financeOrgId }, withCredentials: true })
      .then(r => setFinanceViewerGranted(!!r.data?.enabled)).catch(() => setFinanceViewerGranted(false));
  }, [financeOrgId, user]);
  const RoleIcon = roleConfig.icon;

  // Role-specific default tab: the Scheduler role must land on the Schedule
  // grid the first time it opens the dashboard. We remember per-user in
  // localStorage (key: ol.dashboard.lastRoleSeen) so we only force the
  // default once per role transition — if the scheduler intentionally picks
  // a different tab afterwards, we respect that.
  const roleDefaultRef = useRef(false);
  const ROLE_DEFAULT_TAB = { scheduler: 'schedule', treasurer: 'finance', accountant: 'finance' };
  useEffect(() => {
    if (roleDefaultRef.current || !user?.id) return;
    const dflt = ROLE_DEFAULT_TAB[primaryRole];
    if (!dflt) { roleDefaultRef.current = true; return; }
    try {
      const lastSeen = localStorage.getItem('ol.dashboard.lastRoleSeen');
      if (lastSeen !== `${user.id}:${primaryRole}`) {
        setActiveTab(dflt);
        localStorage.setItem('ol.dashboard.lastRoleSeen', `${user.id}:${primaryRole}`);
      }
    } catch {}
    roleDefaultRef.current = true;
  }, [primaryRole, user?.id]);
  // tw-safelist (JIT needs the literal): data-[state=active]:bg-emerald-600 data-[state=active]:text-white

  // WebSocket real-time notifications
  const orgIdForWs = organizations?.[0]?.id || '';
  const { notifications: wsNotifications, connected: wsConnected } = useNotifications(orgIdForWs, user?.id);

  // Coach-specific: determine assigned team(s) and label
  const coachTeamIds = primaryRole === 'coach'
    ? (user?.roles?.filter(r => r.role_type === 'coach').flatMap(r => r.team_ids || []) || [])
    : [];
  const coachType = primaryRole === 'coach'
    ? (user?.roles?.find(r => r.role_type === 'coach')?.coach_type || 'head')
    : null;

  // Build the role display label
  const getRoleLabel = () => {
    if (primaryRole !== 'coach') return roleConfig.label;
    const assignedTeam = teams.find(t => coachTeamIds.includes(t.id));
    const typeLabel = coachType === 'assistant' ? 'Asst Coach' : 'Head Coach';
    return assignedTeam ? `${typeLabel} — ${assignedTeam.name}` : typeLabel;
  };

  // Filter teams for coach visibility
  const visibleTeams = primaryRole === 'coach' ? teams.filter(t => coachTeamIds.includes(t.id)) : teams;

  // Check if assistant coaches are restricted (org setting)
  const assistantCoachRestricted = primaryRole === 'coach' && coachType === 'assistant' && 
    organizations[0]?.settings?.assistant_coach_restricted;

  // If the persisted/active tab isn't available to the current role (e.g.
  // Overview was removed for the Tech Specialist), fall back to the first
  // tab the role can actually see — computed synchronously so an invalid
  // tab never mounts (and the panel is never blank).
  // A page must clear BOTH gates to appear: the role filter (who the page is
  // for) and the subscription gate (whether this organization bought it). They
  // are independent — "the role allows it but the tier does not" means hidden.
  // `restrict` counts as visible: the tab shows greyed so a customer can see
  // what they have not bought, which is what makes a trial sell.
  // Collapsed state is a per-viewer convenience, so it lives in
  // localStorage and must survive it being unavailable (private windows).
  const [navMobileOpen, setNavMobileOpen] = useState(false);
  // Sidebar starts COLLAPSED (icons + BOP's › edge button to expand) — AJ 2026-10-19,
  // OL_UI_DESIGN_GUIDE §1a. A new key so everyone starts collapsed once; after that
  // the browser remembers the person's own choice.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('ol.nav.collapsed.v2') !== '0'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('ol.nav.collapsed.v2', navCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [navCollapsed]);

  const navFeatureOk = (i) => entitlements.isVisible(entitlements.gateFor(i));
  // navItemsFor = the role's rows in its menu order (ROLE_NAV_ORDER), so the
  // first visible page — the landing page — follows the same order.
  const visibleTabValues = navItemsFor(primaryRole)
    .filter(i => !(i.hideWhenAssistant && assistantCoachRestricted))
    .filter(i => navFeatureOk({ ...i, ...(PAGE_META[i.value] || {}) }))
    .map(i => i.value);
  const effectiveTab = visibleTabValues.includes(activeTab)
    ? activeTab
    : (visibleTabValues[0] || activeTab);
  useEffect(() => {
    if (effectiveTab !== activeTab) {
      // AD / HQ: a remembered Family Directory tab now lives in Registration
      if (activeTab === 'families' && visibleTabValues.includes('registration-hub')) {
        setRegHubTab('families'); setActiveTab('registration-hub'); return;
      }
      // AD: a remembered Organizations / History menu now opens its page's tab
      if (activeTab === 'organizations' && visibleTabValues.includes('overview')) {
        setOverviewTab('organization'); setActiveTab('overview'); return;
      }
      if (activeTab === 'history' && visibleTabValues.includes('stats')) {
        setStatsTab('history'); setActiveTab('stats'); return;
      }
      // AD: the old Finance report viewer is now AD Finance ▸ Reports
      if (activeTab === 'finance-reports' && visibleTabValues.includes('ad-finance')) {
        setActiveTab('ad-finance'); return;
      }
      setActiveTab(effectiveTab);
    }
  }, [effectiveTab, activeTab]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/api/leagues`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/organizations`).catch(() => ({ data: [] })),
      ];

      // Add role-specific requests
      if (primaryRole === 'family') {
        requests.push(axios.get(`${API_URL}/api/families`).catch(() => ({ data: [] })));
      }

      const [leaguesRes, orgsRes, familiesRes] = await Promise.all(requests);
      
      setLeagues(leaguesRes.data);
      setOrganizations(orgsRes.data);
      
      if (familiesRes) {
        setFamilies(familiesRes.data);
        // Fetch players for family
        if (familiesRes.data.length > 0) {
          const playersRes = await axios.get(
            `${API_URL}/api/families/${familiesRes.data[0].id}/players`
          ).catch(() => ({ data: [] }));
          setPlayers(playersRes.data);
        }
      }

      // Fetch teams, schedule summary, sports, and seasons if org available.
      // Sports come from `/api/game-schedule/{org}/sports` which returns the
      // 3 canonical rows (basketball/baseball/volleyball) with `id`+`slug`+`label`,
      // matching the bridge SPORTS.md contract. Seasons come from the canonical
      // `db.seasons` collection via the ol-canonical endpoint.
      if (orgsRes.data.length > 0) {
        const orgId = orgsRes.data[0].id;
        const [teamsRes, summaryRes, sportsRes, seasonsRes] = await Promise.all([
          axios.get(`${API_URL}/api/organizations/${orgId}/teams`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/game-schedule/${orgId}/summary`).catch(() => ({ data: { total_slots: 0, pending: 0, confirmed: 0, tbd: 0 } })),
          axios.get(`${API_URL}/api/game-schedule/${orgId}/sports`).catch(() => ({ data: { sports: [] } })),
          axios.get(`${API_URL}/api/ol-canonical/seasons/${orgId}`).catch(() => ({ data: { data: [] } })),
        ]);
        setTeams(teamsRes.data);
        setScheduleSummary(summaryRes.data);
        setSports(sportsRes.data?.sports || []);
        // Sort seasons newest-first by slug (slugs are YYYY-YYYY). Keep the
        // backend-computed is_current flag (the ONE org-level school-year flip).
        const seasonList = (seasonsRes.data?.data || [])
          .map(s => ({ id: s.id, slug: s.slug || s.title, title: s.title || s.slug, is_current: !!s.is_current }))
          .filter(s => s.slug)
          .sort((a, b) => (b.slug || '').localeCompare(a.slug || ''));
        setSeasons(seasonList);
        // Default the roster Season filter to the current season (the backend's
        // is_current marker), falling back to the most recent season otherwise.
        if (seasonList.length > 0) {
          const currentLabel = (seasonList.find(s => s.is_current) || seasonList[0]).slug;
          const preferred = seasonList.find(s => s.slug === currentLabel)?.slug || seasonList[0].slug;
          setRosterSeasonFilter(prev => prev || preferred);
        }
        
        // Auto-select team: for coaches, select their assigned team
        const coachTeams = user?.roles?.filter(r => r.role_type === 'coach').flatMap(r => r.team_ids || []) || [];
        if (coachTeams.length > 0 && !selectedTeamId) {
          setSelectedTeamId(coachTeams[0]);
        } else if (teamsRes.data.length > 0 && !selectedTeamId) {
          setSelectedTeamId(teamsRes.data[0].id);
        }

        // Fetch roster count for coaches
        if (primaryRole === 'coach' && coachTeams.length > 0) {
          try {
            const rosterRes = await axios.get(`${API_URL}/api/roster/team/${coachTeams[0]}?roster_type=active`);
            setPlayers(rosterRes.data?.players || []);
          } catch { /* ok - will show 0 */ }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    
    try {
      const data = {
        name: leagueForm.name,
        code: leagueForm.code.toUpperCase(),
        divisions: leagueForm.divisions ? leagueForm.divisions.split(',').map(d => d.trim()) : []
      };
      
      await axios.post(`${API_URL}/api/leagues`, data);
      setShowCreateLeague(false);
      setLeagueForm({ name: '', code: '', divisions: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create league');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    
    try {
      const data = {
        name: orgForm.name,
        founding_year: parseInt(orgForm.founding_year),
        league_id: orgForm.league_id,
        divisions: orgForm.divisions ? orgForm.divisions.split(',').map(d => d.trim()) : [],
        sports: orgForm.sports ? orgForm.sports.split(',').map(s => s.trim()) : []
      };
      
      await axios.post(`${API_URL}/api/organizations`, data);
      setShowCreateOrg(false);
      setOrgForm({ name: '', founding_year: new Date().getFullYear(), league_id: '', divisions: '', sports: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreateLoading(false);
    }
  };

  // Current season label = the backend-computed current season (the ONE
  // org-level school-year flip), falling back to the newest loaded season.
  const getSeasonLabel = () => {
    if (!seasons || seasons.length === 0) return '';
    return (seasons.find(s => s.is_current) || seasons[0]).slug;
  };

  // Render overview stats based on role
  const renderStats = () => {
    const stats = [];

    if (['company_support', 'league_admin'].includes(primaryRole)) {
      stats.push(
        { label: 'Leagues', value: leagues.length, icon: Shield, color: 'text-amber-500/50' },
        { label: 'Organizations', value: organizations.length, icon: Building2, color: 'text-blue-500/50' }
      );
    }

    if (['company_support', 'league_admin', 'ad'].includes(primaryRole)) {
      stats.push(
        { label: 'Teams', value: teams.length, icon: Users, color: 'text-green-500/50' }
      );
    }

    if (primaryRole === 'family') {
      stats.push(
        { label: 'My Players', value: players.length, icon: UserCircle, color: 'text-cyan-500/50' },
        { label: 'Registrations', value: players.filter(p => p.current_teams?.length > 0).length, icon: ClipboardList, color: 'text-green-500/50' }
      );
    }

    if (primaryRole === 'coach') {
      stats.push(
        { label: 'My Teams', value: coachTeamIds.length, icon: Users, color: 'text-green-500/50' },
        { label: 'Players', value: players.length, icon: UserCircle, color: 'text-cyan-500/50' }
      );
    }

    if (primaryRole === 'scheduler') {
      stats.push(
        { label: 'Scheduled Games', value: scheduleSummary.total_slots, icon: Calendar, color: 'text-purple-500/50' },
        { label: 'Pending', value: scheduleSummary.pending + scheduleSummary.tbd, icon: ClipboardList, color: 'text-amber-500/50' }
      );
    }

    // Always add scheduled games for non-family/non-scheduler roles
    if (!['family', 'scheduler'].includes(primaryRole)) {
      stats.push(
        { label: 'Scheduled Games', value: scheduleSummary.total_slots, icon: Calendar, color: 'text-purple-500/50' }
      );
    }

    return stats.slice(0, 4);
  };

  // The page title is the active nav item's own label, so it can never
  // disagree with the menu entry the user just clicked.
  const currentPageLabel = (() => {
    const item = NAV_ITEMS.find(i => i.value === effectiveTab);
    return item ? pageLabel(item, primaryRole) : '';
  })();

  const contentOffset = navCollapsed ? 'md:ml-14' : 'md:ml-56';

  return (
    // `bop-skin` carries BackOfficePro's own theme tokens and fonts (Public
    // Sans / Chivo / JetBrains Mono), ported verbatim in styles/bop-admin-skin.css.
    // The canvas colour is BOP's measured #FAFAFA. See docs/OL_UI_DESIGN_GUIDE.md.
    <div className="bop-skin min-h-screen theme-transition bg-[#FAFAFA]">
      {/* BOP's shell: the sidebar owns the FULL left column from the very top,
          and the header lives in the content column to its right. Nothing is
          allowed above the sidebar. See docs/OL_UI_DESIGN_GUIDE.md. */}
      <DashboardSidebar
        items={navItemsFor(primaryRole)
          .filter(i => !(i.hideWhenAssistant && assistantCoachRestricted))
          .filter(i => visibleTabValues.includes(i.value))}
        activeTab={effectiveTab}
        onSelect={setActiveTab}
        entitlements={entitlements}
        primaryRole={primaryRole}
        collapsed={navCollapsed}
        onToggleCollapse={() => setNavCollapsed(v => !v)}
        // BOP UI_GUIDE §3: the badge is the SUBSCRIPTION level for customer
        // roles and nothing for the platform operator. Never the role name.
        badge={isSemprionAdmin ? null
          : (entitlements.unmanaged ? null : (entitlements.tierLabel || entitlements.tier))}
        mobileOpen={navMobileOpen}
        onCloseMobile={() => setNavMobileOpen(false)}
      />

      <div className={`${contentOffset} flex flex-col min-h-screen transition-all duration-300`}>
      {/* Header */}
      <header className="bg-white border-b border-[#DCDFE4] sticky top-0 z-30 h-14 box-border">
        <div className="w-full px-4 lg:px-6 h-full flex items-center justify-between gap-3">
          {/* The product brand lives in the SIDEBAR now, as BOP's does. The
              header's left carries the hamburger (the fixed column is hidden
              below md) and the organization, which is what BOP shows here. */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setNavMobileOpen(true)}
              data-testid="mobile-menu-toggle"
              title="Menu"
              className={`md:hidden -ml-1 p-2 rounded-md ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* The CUSTOMER's logo and name, exactly where BOP shows Taco
                Roos. The platform brand lives in the sidebar. */}
            {organizations[0]?.logo_url && (
              <img
                src={organizations[0].logo_url.startsWith('http')
                  ? organizations[0].logo_url
                  : `${API_URL}${organizations[0].logo_url}`}
                alt={`${organizations[0].name} logo`}
                className="h-8 max-w-[140px] object-contain shrink-0"
                data-testid="header-org-logo"
              />
            )}
            <h1 className="text-base font-semibold truncate text-foreground"
                data-testid="header-org-name">
              {organizations[0]?.name || 'OmniLeagues'}
            </h1>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-right hidden sm:block">
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="user-name">
                {user?.first_name} {user?.last_name}
              </p>
              <div className="flex items-center gap-1 justify-end">
                <Badge className={`${roleConfig.color} text-white text-xs`} data-testid="user-role">
                  {getRoleLabel()}
                </Badge>
                {(user?.roles?.length || 0) > 1 && (
                  <button onClick={() => navigate('/select-role')} data-testid="switch-role-btn"
                    className="ml-2 text-xs underline text-slate-400 hover:text-teal-400">Switch role</button>
                )}
              </div>
            </div>
            <Badge className={`${roleConfig.color} text-white text-xs sm:hidden`} data-testid="user-role-mobile">
              {getRoleLabel()}
            </Badge>
            
            {/* Notifications */}
            <NotificationBell />
            
            {/* WebSocket Status Indicator */}
            <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${wsConnected ? 'bg-green-500/10 text-green-500' : 'bg-slate-500/10 text-slate-500'}`} 
              title={wsConnected ? `Live: ${wsNotifications.length} updates` : 'Offline'} data-testid="ws-indicator">
              <span className={`h-1.5 w-1.5 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
            </div>
            
            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              className={`${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
              data-testid="theme-toggle-btn"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            
            <ChangePasswordDialog
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  title="Change password"
                  className={`${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
                  data-testid="change-password-trigger"
                >
                  <KeyRound className="h-5 w-5" />
                </Button>
              }
            />

            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              className={`${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {impersonatingState && (
        <div className="w-full px-4 lg:px-6 pt-2">
          <button
            onClick={stopImpersonate}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 transition"
            data-testid="stop-impersonation-btn"
            title="Click to end the impersonation session and return to /admin"
          >
            <LogOut className="h-3 w-3 rotate-180" />
            Impersonating — click to exit
          </button>
        </div>
      )}

      <SystemMessageBanner />
      <TrialRibbon />

      <main className="w-full px-4 lg:px-6 py-3">
        {/* BOP leads every page with a Chivo 30px/900 title and a muted
            subtitle, then goes straight to content. OL had a "Welcome, <role>"
            card here that repeated the organization already shown in the header
            and had no BOP equivalent; its two stats moved up beside the title. */}
        <div className="flex items-end justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-[30px] leading-tight font-black tracking-tight text-[#151B28] font-[Chivo]"
                data-testid="page-title">
              {currentPageLabel}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">
              {organizations[0]?.name || ''}{organizations[0]?.name ? ' · ' : ''}{getRoleLabel()}
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {renderStats().map((stat, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
                <div className="leading-tight">
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-sm font-bold text-foreground">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={effectiveTab} onValueChange={setActiveTab} className="space-y-2">
          {/* The wrapping tab strip became unusable as roles grew — Semprion HQ
              has 34 pages, a league admin 17. The sidebar renders the SAME
              filtered list and asks entitlements the SAME two questions; it
              adds grouping and vertical room, not new visibility rules. */}

          {/* Staff TabsContent removed — see TabsTrigger note above. The
              Org Hub renders the same panel via OrgStaffPanel. */}

          {/* Family Directory Tab — hub with Directory | Players | Birthdays */}
          <TabsContent value="families">
            {organizations.length > 0 && (
              <FamilyDirectoryHubPanel orgId={organizations[0]?.id} teams={visibleTeams} isDark={isDark} />
            )}
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Treasurer sees only a pointer to Finance here — no admin Quick Actions / Settings card. */}
            {primaryRole === 'treasurer' && (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="treasurer-overview-note">
                <CardContent className="p-6">
                  <p className={isDark ? 'text-slate-200' : 'text-slate-800'}>Welcome, Treasurer. Open the <span className="font-semibold text-emerald-500">Finance</span> tab to manage the books, chart of accounts, journal and reports.</p>
                </CardContent>
              </Card>
            )}
            {primaryRole === 'ad' && (
              <PageSubTabs value={overviewTab} onChange={setOverviewTab} isDark={isDark} testid="overview-subtabs"
                tabs={[['briefing', 'Morning Briefing'], ['organization', 'Organization']]} />
            )}
            {primaryRole === 'ad' && overviewTab === 'organization' && organizations.length > 0 && (
              <OrganizationsHubPanel orgId={organizations[0]?.id} isDark={isDark} />
            )}
            {(primaryRole !== 'ad' || overviewTab === 'briefing') && (<>
            {/* Morning Briefing - AD+ only */}
            {/* AD Overview widgets — Financial / Program / Season (AD_DASHBOARD_AND_FINANCE.md §1) */}
            {primaryRole === 'ad' && organizations.length > 0 && (
              <AdOverviewWidgets orgId={organizations[0]?.id} financeOrgId={financeOrgId}
                reportsGranted={financeViewerGranted} onNavigate={setActiveTab} />
            )}
            {['company_support', 'league_admin', 'ad'].includes(primaryRole) && organizations.length > 0 && (
              <MorningBriefing orgId={organizations[0]?.id} />
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leagues Section - Admin Only */}
              {['company_support', 'league_admin'].includes(primaryRole) && (
                <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className={`${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2 text-lg`}>
                        <Shield className="h-5 w-5 text-amber-500" />
                        Leagues
                      </CardTitle>
                    </div>
                    {roleConfig.canCreateLeague && (
                      <Button 
                        size="sm" 
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => setShowCreateLeague(true)}
                        data-testid="create-league-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
                      </div>
                    ) : leagues.length === 0 ? (
                      <p className="text-slate-400 text-center py-4">No leagues yet</p>
                    ) : (
                      <div className="space-y-2">
                        {leagues.map((league) => (
                          <div 
                            key={league.id}
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-slate-50 hover:bg-slate-100'}`}
                            onClick={() => setActiveTab('organizations')}
                            data-testid={`league-${league.code}`}
                          >
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{league.name}</p>
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{league.code} &middot; {league.divisions?.length || 0} divisions</p>
                            </div>
                            <ChevronRight className={`h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Organizations Section — visible to admins & league admins.
                  Removed from AD Overview in Session 19 (May 12 2026) per
                  user feedback that the AD's dashboard didn't need a card
                  pointing at their own org. */}
              {['company_support', 'league_admin'].includes(primaryRole) && (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className={`${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2 text-lg`}>
                      <Building2 className="h-5 w-5 text-blue-500" />
                      {primaryRole === 'family' ? 'My Organization' : 'Organizations'}
                    </CardTitle>
                  </div>
                  {roleConfig.canCreateOrg && leagues.length > 0 && (
                    <Button 
                      size="sm" 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowCreateOrg(true)}
                      data-testid="create-org-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                  ) : organizations.length === 0 ? (
                    <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'} text-center py-4`}>No organizations yet</p>
                  ) : (
                    <div className="space-y-2">
                      {organizations.map((org) => (
                        <div 
                          key={org.id}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-slate-50 hover:bg-slate-100'}`}
                          onClick={() => setActiveTab('teams')}
                          data-testid={`org-${org.id}`}
                        >
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{org.name}</p>
                            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              Founded {org.founding_year} &middot; {org.sports?.length || 0} sports
                            </p>
                          </div>
                          <ChevronRight className={`h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              )}

              {/* Teams Section - for Coach/Scheduler. Removed from AD
                  Overview in Session 19 (May 12 2026) — the AD has a
                  dedicated Teams & Costs tab + Roster tab + Organisations
                  hub for team management; an extra Overview card was
                  redundant. */}
              {['coach', 'scheduler'].includes(primaryRole) && (
                <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className={`${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2 text-lg`}>
                        <Users className="h-5 w-5 text-green-500" />
                        {primaryRole === 'coach' ? 'My Team' : 'Teams'}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {visibleTeams.length === 0 ? (
                      <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'} text-center py-4`}>No teams assigned</p>
                    ) : (
                      <div className="space-y-2">
                        {visibleTeams.map((team) => (
                          <div 
                            key={team.id}
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-slate-50 hover:bg-slate-100'}`}
                          >
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{team.name}</p>
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                Max Age: {team.eligibility?.max_age} · ${team.season_cost}
                                {team.head_coach_id && ` · HC assigned`}
                              </p>
                            </div>
                            <Badge className="bg-green-600">{team.code}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Family Players Section */}
              {primaryRole === 'family' && (
                <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className={`${isDark ? 'text-white' : 'text-slate-900'} flex items-center gap-2 text-lg`}>
                        <UserCircle className="h-5 w-5 text-cyan-500" />
                        My Players
                      </CardTitle>
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-cyan-600 hover:bg-cyan-700"
                      onClick={() => setShowRegistrationCart(true)}
                      data-testid="add-player-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-1" /> Register Player
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {players.length === 0 ? (
                      <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'} text-center py-4`}>No players registered</p>
                    ) : (
                      <div className="space-y-2">
                        {players.map((player) => (
                          <div 
                            key={player.id}
                            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${isDark ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-slate-50 hover:bg-slate-100'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center text-white font-bold">
                                {player.jersey_number || player.first_name?.[0]}
                              </div>
                              <div>
                                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  {player.first_name} {player.last_name}
                                </p>
                                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                  {player.gender === 'male' ? 'M' : 'F'} &middot; DOB: {player.birthday}
                                </p>
                              </div>
                            </div>
                            <Badge className={player.current_teams?.length > 0 ? 'bg-green-600' : 'bg-slate-600'}>
                              {player.current_teams?.length > 0 ? 'Registered' : 'Not Registered'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
            </>)}
          </TabsContent>

          {/* Library — program documents as quick links (LIBRARY.md) */}
          <TabsContent value="library">
            {organizations.length > 0 && <LibraryPanel orgId={organizations[0]?.id} />}
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
              <CardHeader>
                <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>Team Management</CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  View and manage teams in your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.map((team) => (
                    <Card key={team.id} className={isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Badge className="bg-amber-600">{team.code}</Badge>
                          <span className="text-lg font-bold text-green-400">${team.season_cost}</span>
                        </div>
                        <h3 className={`${isDark ? 'text-white' : 'text-slate-900'} font-semibold mb-1`}>{team.name}</h3>
                        <div className={`space-y-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          <p>Max Age: {team.eligibility?.max_age} (cutoff {team.eligibility?.cutoff_month}/{team.eligibility?.cutoff_day})</p>
                          <p>Game Duration: {team.game_duration_minutes} min</p>
                          <p>Coaches: {team.coaches?.length || 0}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Players Tab (Family) */}
          <TabsContent value="players">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
              <CardHeader>
                <CardTitle className={isDark ? 'text-white' : 'text-slate-900'}>My Players</CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  Manage your family&apos;s player registrations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {players.map((player) => (
                    <Card key={player.id} className={isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                            {player.headshot_url ? (
                              <img src={`${API_URL}${player.headshot_url}`} alt={player.first_name} className="w-full h-full object-cover" />
                            ) : (
                              player.jersey_number || player.first_name?.[0]
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className={`${isDark ? 'text-white' : 'text-slate-900'} font-semibold text-lg`}>
                              {player.first_name} {player.last_name}
                            </h3>
                            <p className={`${isDark ? 'text-slate-400' : 'text-slate-500'} text-sm`}>
                              {player.gender === 'male' ? 'Male' : 'Female'} &middot; Born {player.birthday}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={player.health_status?.is_healthy ? 'bg-green-600' : 'bg-red-600'}>
                                {player.health_status?.is_healthy ? 'Healthy' : 'Health Note'}
                              </Badge>
                              {player.jersey_number && (
                                <Badge className="bg-amber-600">#{player.jersey_number}</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <HeadshotManager player={player} onUpdate={(url) => fetchData()} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Registration Tab (Family) */}
          <TabsContent value="registration">
            {organizations.length > 0 ? (
              <RegistrationFormPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available for registration</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Registration hub (AD + Tech Specialist + Scheduler, identical) — Registrations /
              Programs / Financials / Invoices / Communications / Rostering / Settings */}
          <TabsContent value="registration-hub">
            {organizations.length > 0 ? (
              <RegistrationHubPanel orgId={organizations[0]?.id} isDark={isDark} userRole={primaryRole}
                initialTab={regHubTab} onInitialConsumed={clearRegHubTab}
                extraTabs={[
                  ...(['ad', 'scheduler'].includes(primaryRole) ? [{ key: 'draft-board', label: 'Draft Board', icon: Users,
                    render: () => <DraftBoardPanel orgId={organizations[0]?.id} /> }] : []),
                  ...(['ad', 'scheduler', 'company_support'].includes(primaryRole) ? [{ key: 'families', label: 'Family Directory', icon: Users,
                    render: () => <FamilyDirectoryHubPanel orgId={organizations[0]?.id} teams={visibleTeams} isDark={isDark} /> }] : []),
                ]} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>


          {/* Schedule Tab */}
          <TabsContent value="schedule">
            {organizations.length > 0 ? (
              <div className="space-y-6">
                {/* Primary tool: SchedulerPanel hosts the full Scheduler workspace.
                    Its default sub-view is "Game Schedule" which renders
                    GameSchedulePanel (spreadsheet/List view by default — matches
                    the Sabres Google Sheet layout with team columns and
                    per-date/opponent rows). Other sub-views: Calendar · Requests
                    · Blocked · Interests · MSTW.
                    Scheduler drafts locally; Publish is what eventually pushes
                    to WordPress (TBD-aware, per DATA_SCHEMAS.md). */}
                {/* SCHEDULER: the hub owns the page — a common button row, one tab
                    per configured sport (Basketball first/default), and the per-sport
                    sub-tabs. CS/AD keep the original single-workspace panel.
                    See SCHEDULER_ROLE_PAGE.md. */}
                {primaryRole === 'scheduler' ? (
                  <SchedulerHub
                    orgId={organizations[0]?.id}
                    teams={visibleTeams}
                    userRole={primaryRole}
                    user={user}
                    familyId={families[0]?.id}
                    isDark={isDark}
                  />
                ) : ['company_support', 'ad'].includes(primaryRole) && (
                  <SchedulerPanel
                    orgId={organizations[0]?.id}
                    teams={visibleTeams}
                    userRole={primaryRole}
                  />
                )}
                {/* Opponent Booking (for coaches requesting away games) */}
                {primaryRole === 'coach' && organizations[0]?.id && (
                  <OpponentBookingPanel
                    hostOrgId={organizations[0]?.id}
                    hostOrgName={organizations[0]?.name}
                    requestingOrgId={organizations[0]?.id}
                    requestingTeamId={visibleTeams[0]?.id}
                    requestingTeamName={visibleTeams[0]?.name}
                  />
                )}
              </div>
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Schedule v3 Tab */}
          <TabsContent value="schedule-v3">
            {organizations.length > 0 ? (
              <div className="bg-slate-900 rounded-lg p-4 -mx-2">
                <AdminManager orgId={organizations[0]?.id} />
              </div>
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Booking Calendar (OL-BookMe) Tab */}
          <TabsContent value="booking">
            {organizations.length > 0 ? (
              <BookingCalendarPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* RSVP Tab */}
          <TabsContent value="rsvp">
            {primaryRole === 'family' && families.length > 0 ? (
              <RSVPPanel familyId={families[0]?.id} mode="family" />
            ) : organizations.length > 0 ? (
              <AvailabilityPanel
                orgId={organizations[0]?.id}
                teams={visibleTeams}
                userRole={primaryRole}
                familyId={families[0]?.id}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Volunteer Tab */}
          <TabsContent value="volunteer">
            {organizations.length > 0 ? (
              <VolunteerPanel 
                orgId={organizations[0]?.id}
                teamId={user?.roles?.[0]?.team_ids?.[0]}
                canManage={['company_support', 'ad', 'coach'].includes(primaryRole)}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            {organizations.length > 0 ? (<>
              {/* Player Documents (AD_DASHBOARD_AND_FINANCE.md §3): registration paperwork + document alerts */}
              <PageSubTabs value={docsTab} onChange={setDocsTab} isDark={isDark} testid="documents-subtabs"
                tabs={[['paperwork', 'Waivers & Birth Certificates'], ['alerts', 'Document Alerts']]} />
              {docsTab === 'paperwork'
                ? <PlayerDocumentsSummary orgId={organizations[0]?.id} />
                : <DocumentAlerts orgId={organizations[0]?.id} />}
            </>) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Payments Tab — AD sees the shared Financials workspace (same data
              as Registration > Financials). CS/LA keep the classic PaymentPanel;
              families keep their own PaymentPanel. */}
          <TabsContent value="payments">
            {primaryRole === 'ad' ? (
              organizations.length > 0 ? (
                <FinancialsPanel orgId={organizations[0]?.id} isDark={isDark} canConfigureProcessor={false} />
              ) : (
                <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                  <CardContent className="p-8 text-center">
                    <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                  </CardContent>
                </Card>
              )
            ) : (
              <PaymentPanel 
                orgId={organizations[0]?.id}
                isAdmin={['company_support', 'ad'].includes(primaryRole)}
              />
            )}
          </TabsContent>

          {/* Roster Tab
              ───────────────
              Session 19 v3 (May 12 2026): AD told us the team-button row was
              showing every team multiple times (MSTW shadow opponents + the
              real OL teams blended). Now we filter to the AD-managed OL
              teams (those carrying both `code` and `sport_id`) and add
              Season + Sport dropdowns above the team buttons.

              Session 20 (Feb 18 2026): Sport name was rendering as the
              literal string "Sport" because the previous implementation
              used a `typeof sports !== 'undefined'` guard against state
              that was never declared. Now we fetch sports + seasons from
              the canonical endpoints in fetchData() and consume them here.
              Seasons are no longer hardcoded — derived from `db.seasons`.

              Session 20 (revision per user feedback): pills are
              sport-AGNOSTIC level chips — same "Varsity Boys" can belong
              to multiple sports. We render one chip per unique team name
              (the level), no sport prefix. Sport dropdown lists ALL
              canonical sports from `db.sports`, not just sports that
              currently have teams — the AD wants the same shape from
              day one even before baseball/volleyball teams exist. The
              team card's own season dropdown is suppressed when the
              page-level filter is present (avoids the dup picker). */}
          <TabsContent value="roster">
            {(() => {
              const adTeams = visibleTeams.filter(t => t.code && t.sport_id);
              // Derive a coarse "level" from team.code so the AD can
              // filter Varsity / JV / Middle School independently of
              // sport. Mapping is intentionally simple (first 1-2 chars
              // of the level token in code: VB/VG → Varsity, JVB/JVG →
              // JV, MSB/MSG/MSB-D → MS).
              const levelFromCode = (code) => {
                const c = (code || '').toUpperCase();
                if (c.startsWith('MS')) return 'ms';
                if (c.startsWith('JV')) return 'jv';
                if (c.startsWith('V'))  return 'varsity';
                if (c.startsWith('YTH') || c.startsWith('Y')) return 'youth';
                return 'other';
              };
              const LEVEL_LABELS = { varsity: 'Varsity', jv: 'JV',
                                     ms: 'Middle School', youth: 'Youth',
                                     other: 'Other' };
              // Sport dropdown options = ALL canonical sports (3) from
              // `db.sports`, sorted by name. Sport-agnostic levels means
              // we don't gate this dropdown by "has teams yet".
              const sportOptions = sports
                .map(s => ({ id: s.id, name: s.label || s.name || s.slug }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              const sportScopedTeams = rosterSportFilter
                ? adTeams.filter(t => t.sport_id === rosterSportFilter)
                : [];
              // Level options derived from the teams that actually exist
              // for the currently-selected sport.
              const levelOptions = (() => {
                const seen = new Set();
                const out = [];
                for (const t of sportScopedTeams) {
                  const lvl = levelFromCode(t.code);
                  if (seen.has(lvl)) continue;
                  seen.add(lvl);
                  out.push({ v: lvl, l: LEVEL_LABELS[lvl] || lvl });
                }
                // Stable display order
                const order = ['varsity', 'jv', 'ms', 'youth', 'other'];
                return out.sort((a, b) => order.indexOf(a.v) - order.indexOf(b.v));
              })();
              const filteredTeams = rosterLevelFilter
                ? sportScopedTeams.filter(t => levelFromCode(t.code) === rosterLevelFilter)
                : sportScopedTeams;
              // Pills = unique level/team names. When the same level name
              // exists in multiple sports we'd show one pill; clicking it
              // loads the first matching team. (Today this is moot — only
              // basketball has teams — but it future-proofs correctly.)
              const levelPills = [];
              for (const t of filteredTeams) {
                if (levelPills.find(p => p.name === t.name)) continue;
                levelPills.push({ name: t.name, teamId: t.id, code: t.code });
              }
              const seasonOptions = seasons.length > 0
                ? seasons.map(s => s.slug)
                : [];
              const selectedSportName = sportOptions.find(s => s.id === rosterSportFilter)?.name || '';
              // Drop a stale selection when sport changes
              const teamStillValid = !!selectedTeamId && filteredTeams.some(t => t.id === selectedTeamId);

              return adTeams.length > 0 ? (
                <div className="space-y-4">
                  {/* Cascading filter bar: Season → Sport → Level.
                      Per AD spec each step narrows the displayed data. The
                      Sport dropdown is a required step (defaults to a
                      placeholder); Level pills only render once a sport
                      has been chosen. */}
                  <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
                    <CardContent className="p-3 flex flex-wrap items-center gap-3">
                      <span className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Filters</span>
                      <div className="flex items-center gap-2">
                        <label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Season</label>
                        <select
                          value={rosterSeasonFilter}
                          onChange={(e) => setRosterSeasonFilter(e.target.value)}
                          className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
                          data-testid="roster-season-filter"
                        >
                          {seasonOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Sport</label>
                        <select
                          value={rosterSportFilter}
                          onChange={(e) => { setRosterSportFilter(e.target.value); setRosterLevelFilter(''); setSelectedTeamId(null); }}
                          className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
                          data-testid="roster-sport-filter"
                        >
                          <option value="">— Select a sport —</option>
                          {sportOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      {rosterSportFilter && levelOptions.length > 0 && (
                        <div className="flex items-center gap-2">
                          <label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Level</label>
                          <select
                            value={rosterLevelFilter}
                            onChange={(e) => { setRosterLevelFilter(e.target.value); setSelectedTeamId(null); }}
                            className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
                            data-testid="roster-level-filter"
                          >
                            <option value="">All levels</option>
                            {levelOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                          </select>
                        </div>
                      )}
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} ml-auto`} data-testid="roster-filter-count">
                        {rosterSportFilter
                          ? `${filteredTeams.length} ${selectedSportName} level${filteredTeams.length === 1 ? '' : 's'}`
                          : `${adTeams.length} total team${adTeams.length === 1 ? '' : 's'}`}
                      </span>
                    </CardContent>
                  </Card>

                  {/* Step 3 — Level pills. Only render once a sport is
                      picked. If the chosen sport has zero teams, show an
                      empty-state CTA pointing the AD to Teams & Costs (the
                      canonical place to create level rows). */}
                  {!rosterSportFilter && (
                    <div className={`text-sm italic px-3 py-6 text-center rounded-lg border border-dashed ${isDark ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'}`} data-testid="roster-cascade-hint">
                      Pick a sport above to see its levels.
                    </div>
                  )}
                  {rosterSportFilter && levelPills.length === 0 && (
                    <div className={`px-4 py-6 text-center rounded-lg border border-dashed ${isDark ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'}`} data-testid="roster-empty-levels">
                      <p className="text-sm font-medium mb-2">
                        No {selectedSportName} levels configured yet.
                      </p>
                      <p className="text-xs mb-3">
                        Levels are created as Teams in Teams & Costs. Once you add
                        rows there (e.g. Varsity Boys, JV Girls, …), they appear
                        here automatically.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setTeamsHubTab('org-teams'); setActiveTab('teams-hub'); }}
                        data-testid="roster-open-teams-costs"
                      >
                        Open Teams →
                      </Button>
                    </div>
                  )}
                  {rosterSportFilter && levelPills.length > 0 && (
                    <div className={`flex gap-2 flex-wrap`} data-testid="roster-level-pills">
                      {levelPills.map(p => (
                        <Button
                          key={p.teamId}
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedTeamId(p.teamId)}
                          className={selectedTeamId === p.teamId
                            ? 'bg-amber-600 text-white border-amber-600'
                            : isDark ? 'border-slate-600 text-slate-300' : ''}
                          data-testid={`roster-team-${p.code}`}
                        >
                          {p.name}
                        </Button>
                      ))}
                    </div>
                  )}

                  {teamStillValid && (
                    <RosterPanel
                      teamId={selectedTeamId}
                      orgId={organizations[0]?.id}
                      seasonOverride={rosterSeasonFilter}
                      showExport={['company_support', 'ad', 'coach'].includes(primaryRole)}
                      canManage={['company_support', 'ad', 'coach'].includes(primaryRole)}
                    />
                  )}
                </div>
              ) : (
                <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <CardContent className="p-8 text-center">
                    <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No teams available</p>
                  </CardContent>
                </Card>
              );
            })()}
          </TabsContent>

          {/* Exports Tab */}
          <TabsContent value="exports">
            {organizations.length > 0 ? (
              <ExportPanel orgId={organizations[0]?.id} teams={teams} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Reports Tab — restricted to admins/league_admins (Session 19) */}
          <TabsContent value="reports">
            {!['company_support', 'league_admin'].includes(primaryRole) ? null : organizations.length > 0 ? (
              <ReportsPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
            {primaryRole === 'league_admin' && leagues.length > 0 && (
              <div className="mt-6">
                <LeagueReportsPanel leagueId={leagues[0]?.id} />
              </div>
            )}
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats">
            {primaryRole === 'ad' && (
              <PageSubTabs value={statsTab} onChange={setStatsTab} isDark={isDark} testid="stats-subtabs"
                tabs={[['stats', 'Statistics'], ['history', 'History']]} />
            )}
            {primaryRole === 'ad' && statsTab === 'history' ? (
              <ProgramHistoryPanel orgId={organizations[0]?.id} userRole={primaryRole} />
            ) : organizations.length > 0 ? (
              <StatsPanel orgId={organizations[0]?.id} teams={teams} userRole={primaryRole} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Program History Tab */}
          <TabsContent value="history">
            <ProgramHistoryPanel orgId={organizations[0]?.id} userRole={primaryRole} />
          </TabsContent>

          {/* Communications Tab — per-sport recurring task lists emailed to the
              Steering Committee. Replaces the legacy SWS Program-Schedule
              Google Sheet (Session 23). */}
          {/* Calendar tab
              ───────────────
              Schedulers see the Booking-style calendar where they build /
              modify / publish slots. Session 19 (May 12 2026) — the AD now
              shares the SAME Booking calendar view (read-only is enforced
              by the absence of the "scheduler" role in BookingCalendarPanel's
              write actions) so they see exactly what the Scheduler is
              building for the org. The legacy "CalendarPanel" (Subscription
              card + Upcoming Games + Time Allocation) confused operators
              and duplicated work that lives in Tech Settings + Schedule
              tabs, so it's no longer the default for the AD.
              Coaches and Families continue to see the lightweight
              CalendarPanel which is the right read-only experience for
              them. */}
          <TabsContent value="calendar">
            {organizations.length > 0 ? (
              /* AD gets the SAME Schedule component the Team Scheduler uses (List +
                 Calendar views, every future Schedule improvement) but VIEW-ONLY via
                 the `readOnly` prop — no add/import/publish/settings, no inline edits,
                 no drag-to-reschedule. TS/Scheduler keep the Booking calendar. */
              primaryRole === 'ad' ? (
                <GameSchedulePanel orgId={organizations[0]?.id} readOnly initialView="calendar" />
              ) : ['scheduler', 'league_admin', 'company_support', 'tech_specialist'].includes(primaryRole) ? (
                <BookingCalendarPanel orgId={organizations[0]?.id} />
              ) : (
                <CalendarPanel orgId={organizations[0]?.id} teams={teams} userRole={primaryRole} />
              )
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            {organizations.length > 0 ? (
              <AvailabilityPanel
                orgId={organizations[0]?.id}
                teams={visibleTeams}
                userRole={primaryRole}
                familyId={families[0]?.id}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Messaging Tab */}
          <TabsContent value="messaging">
            {organizations.length > 0 ? (
              <MessagingPanel
                orgId={organizations[0]?.id}
                userId={user?.id}
                userName={`${user?.first_name} ${user?.last_name}`}
                userRole={primaryRole}
                teams={visibleTeams}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Lineup Tab (Coach only) */}
          <TabsContent value="lineup">
            {organizations.length > 0 ? (
              <LineupPanel
                orgId={organizations[0]?.id}
                teams={visibleTeams}
                userRole={primaryRole}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Registration Intake (TS) */}
          {/* Media Tab */}
          <TabsContent value="media">
            {organizations.length > 0 ? (
              <MediaPanel
                orgId={organizations[0]?.id}
                teams={visibleTeams}
                userRole={primaryRole}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tournaments Tab */}
          <TabsContent value="tournaments">
            {organizations.length > 0 ? (
              <TournamentPanel orgId={organizations[0]?.id} userRole={primaryRole} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Seasons Tab (AD+) */}
          <TabsContent value="activity">
            {organizations.length > 0 ? (
              <ActivityLogPanel orgId={organizations[0]?.id} isDark={isDark} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="seasons">
            {organizations.length > 0 ? (
              <SeasonPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Live Scoring Tab (Coach/Scheduler) */}
          <TabsContent value="live-scoring">
            {organizations.length > 0 ? (
              <LiveScoringPanel orgId={organizations[0]?.id} teamId={teams[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Sponsors Tab (AD+) — Session 19: AD now manages sponsors via
              an embedded Media Library scoped to category=sponsor; the WP
              theme picks up sponsor-tagged media on the next sync. */}
          <TabsContent value="sponsors">
            {organizations.length > 0 ? (
              <SponsorsMediaPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* OL Finance (Treasurer + Accountant) — full workspace; the backend
              enforces the accountant's boundary (no user-mgmt / company create). */}
          <TabsContent value="finance">
            <FinanceModule orgId={primaryRole === 'accountant' ? financeOrgId : treasurerOrgId} isDark={isDark} />
          </TabsContent>

          {/* AD Finance — Player Dues, Budget, Reports (the read-only viewer, only when the
              Treasurer has granted it). The Treasurer has the books under Finance, so no Reports tab. */}
          <TabsContent value="ad-finance">
            {(primaryRole === 'treasurer' ? treasurerOrgId : organizations[0]?.id) && (
              <AdFinancePanel orgId={primaryRole === 'treasurer' ? treasurerOrgId : organizations[0]?.id}
                showReports={primaryRole !== 'treasurer' && financeViewerGranted} reportsOrgId={financeOrgId} isDark={isDark}
                showTeamCosts={primaryRole !== 'treasurer'} onOpenTeams={() => setActiveTab('teams-hub')} />
            )}
          </TabsContent>

          {/* Settings Tab (AD+) */}
          <TabsContent value="settings">
            {organizations.length > 0 ? (
              <AdminSettingsPanel orgId={organizations[0]?.id} teams={teams} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* HQ Settings (Semprion HQ / Company Support) — 3rd Party Tools + Org */}
          <TabsContent value="hq-settings">
            <HqSettingsPanel orgId={organizations[0]?.id} teams={teams} />
          </TabsContent>

          {/* Tech Settings Tab (Technology Specialist) */}
          <TabsContent value="tech-settings">
            {organizations.length > 0 ? (
              <TechSettingsPanel orgId={organizations[0]?.id} organizations={organizations} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Website Visibility Tab (System Admin + Technology Specialist) */}
          <TabsContent value="website-visibility">
            {organizations.length > 0 ? (
              <WebsiteVisibilityPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Download Manager Tab (System Admin + Technology Specialist) */}
          <TabsContent value="download-manager">
            {organizations.length > 0 ? (
              <DownloadManagerPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Individual Awards Tab (System Admin + Technology Specialist) */}
          <TabsContent value="individual-awards">
            {organizations.length > 0 ? (
              <IndividualAwardsPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tools Tab (System Admin + Technology Specialist) */}
          <TabsContent value="tools">
            {organizations.length > 0 ? (
              <ToolsPanel orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Communications — Scheduler + AD. News reuses the Tech Specialist's
              Website > News screen so behaviour and destinations stay identical. */}
          <TabsContent value="ad-tools">
            {organizations.length > 0 && <ToolsHub orgId={organizations[0]?.id} />}
          </TabsContent>

          <TabsContent value="site-media">
            {organizations.length > 0 && <MediaPage orgId={organizations[0]?.id} isDark={isDark} />}
          </TabsContent>

          <TabsContent value="contacts">
            {organizations.length > 0 && <ContactsPanel orgId={organizations[0]?.id} />}
          </TabsContent>

          <TabsContent value="teams-hub">
            {organizations.length > 0 ? (
              <TeamsHubPanel orgId={organizations[0]?.id} isDark={isDark} userRole={primaryRole}
                initialTab={teamsHubTab} onInitialConsumed={clearTeamsHubTab} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-6">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    No organizations yet — create one in Organizations first.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>


          <TabsContent value="communications">
            {organizations.length > 0 ? (
              <CommunicationsHub
                orgId={organizations[0]?.id}
                user={user}
                userRole={primaryRole}
                teams={visibleTeams}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Members Tab — Website Members + OmniLeagues Members (CS/TS); League scope (LA) */}
          <TabsContent value="member-mgmt">
            {organizations.length > 0 || (user?.roles || []).some(r => r.role_type === 'league_admin') ? (
              <MembersHub orgId={organizations[0]?.id} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Game Day Tab */}
          <TabsContent value="gameday">
            {organizations.length > 0 ? (
              <GameDayPanel
                orgId={organizations[0]?.id}
                teams={visibleTeams}
                userRole={primaryRole}
                selectedTeamId={primaryRole === 'coach' ? selectedTeamId : null}
              />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Company Tab (Support only) */}
          <TabsContent value="company">
            <CompanyPanel />
          </TabsContent>

          {/* Public Site Preview tab body removed in Session 19 (May 12 2026)
              along with its trigger. See trigger-block comment above for
              rationale. */}

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            {organizations.length > 0 ? (
              <OrganizationsHubPanel orgId={organizations[0]?.id} isDark={isDark} />
            ) : (
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} theme-transition`}>
                <CardContent className="p-6">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    No organizations yet. Create one to get started.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>

        {/* Quick Actions - Role Specific (hidden for Treasurer — Finance-only) */}
        {primaryRole !== 'treasurer' && (
        <div className="mt-8">
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {primaryRole === 'family' && (
              <>
                <Button 
                  variant="outline" 
                  className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  onClick={() => { setActiveTab('registration'); }}
                  data-testid="quick-register-btn"
                >
                  <ShoppingCart className="h-6 w-6" />
                  <span>Register Player</span>
                </Button>
                <Button 
                  variant="outline" 
                  className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  onClick={() => setActiveTab('payments')}
                  data-testid="quick-payment-btn"
                >
                  <DollarSign className="h-6 w-6" />
                  <span>Make Payment</span>
                </Button>
                <Button 
                  variant="outline" 
                  className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  onClick={() => setActiveTab('volunteer')}
                  data-testid="quick-volunteer-btn"
                >
                  <HandHelping className="h-6 w-6" />
                  <span>Volunteer</span>
                </Button>
              </>
            )}
            {['ad', 'coach'].includes(primaryRole) && (
              <>
                <Button 
                  variant="outline" 
                  className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  onClick={() => { if (primaryRole === 'ad') { setRegHubTab('draft-board'); setActiveTab('registration-hub'); } else { setActiveTab('roster'); } }}
                  data-testid="quick-roster-btn"
                >
                  <Users className="h-6 w-6" />
                  <span>{primaryRole === 'ad' ? 'Draft Board' : 'View Roster'}</span>
                </Button>
                <Button 
                  variant="outline" 
                  className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  onClick={() => setActiveTab('volunteer')}
                  data-testid="quick-volunteer-btn"
                >
                  <HandHelping className="h-6 w-6" />
                  <span>Volunteer Tasks</span>
                </Button>
                <Button 
                  variant="outline" 
                  className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                  onClick={() => setActiveTab('documents')}
                  data-testid="quick-documents-btn"
                >
                  <FileWarning className="h-6 w-6" />
                  <span>Player Documents</span>
                </Button>
              </>
            )}
            {primaryRole !== 'ad' && (
            <Button 
              variant="outline" 
              className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              onClick={() => setActiveTab(primaryRole === 'tech_specialist' ? 'tech-settings' : 'settings')}
              data-testid="quick-settings-btn"
            >
              <Settings className="h-6 w-6" />
              <span>{primaryRole === 'tech_specialist' ? 'Tech Settings' : 'Settings'}</span>
            </Button>
            )}
            {primaryRole === 'ad' && (
            <Button 
              variant="outline" 
              className={`h-auto py-4 flex flex-col items-center gap-2 ${isDark ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
              onClick={() => { setTeamsHubTab('org-teams'); setActiveTab('teams-hub'); }}
              data-testid="quick-settings-btn"
            >
              <Settings className="h-6 w-6" />
              <span>Team Settings</span>
            </Button>
            )}
          </div>
        </div>
        )}
      </main>

      {/* Create League Dialog */}
      <Dialog open={showCreateLeague} onOpenChange={setShowCreateLeague}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Create New League</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new athletic association or league
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLeague} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="league-name" className="text-slate-300">League Name</Label>
              <Input
                id="league-name"
                value={leagueForm.name}
                onChange={(e) => setLeagueForm({...leagueForm, name: e.target.value})}
                placeholder="North Carolina Athletic Association"
                required
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="league-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="league-code" className="text-slate-300">League Code</Label>
              <Input
                id="league-code"
                value={leagueForm.code}
                onChange={(e) => setLeagueForm({...leagueForm, code: e.target.value})}
                placeholder="NCHEAC"
                required
                maxLength={20}
                className="bg-slate-700/50 border-slate-600 text-white uppercase"
                data-testid="league-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="league-divisions" className="text-slate-300">Divisions (comma-separated)</Label>
              <Input
                id="league-divisions"
                value={leagueForm.divisions}
                onChange={(e) => setLeagueForm({...leagueForm, divisions: e.target.value})}
                placeholder="Eastern, Western, Central"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="league-divisions-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowCreateLeague(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-600 hover:bg-amber-700"
                disabled={createLoading}
                data-testid="league-submit-btn"
              >
                {createLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create League'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Create New Organization</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Add a sports organization or club
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateOrg} className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="org-name" className="text-slate-300">Organization Name</Label>
              <Input
                id="org-name"
                value={orgForm.name}
                onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
                placeholder="South Wake Sabres"
                required
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="org-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="org-year" className="text-slate-300">Founding Year</Label>
                <Input
                  id="org-year"
                  type="number"
                  value={orgForm.founding_year}
                  onChange={(e) => setOrgForm({...orgForm, founding_year: e.target.value})}
                  min="1900"
                  max={new Date().getFullYear()}
                  required
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="org-year-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-league" className="text-slate-300">League</Label>
                <select
                  id="org-league"
                  value={orgForm.league_id}
                  onChange={(e) => setOrgForm({...orgForm, league_id: e.target.value})}
                  required
                  className="w-full h-10 rounded-md bg-slate-700/50 border border-slate-600 text-white px-3"
                  data-testid="org-league-select"
                >
                  <option value="">Select League</option>
                  {leagues.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-sports" className="text-slate-300">Sports (comma-separated)</Label>
              <Input
                id="org-sports"
                value={orgForm.sports}
                onChange={(e) => setOrgForm({...orgForm, sports: e.target.value})}
                placeholder="Basketball, Baseball, Volleyball"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="org-sports-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowCreateOrg(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={createLoading}
                data-testid="org-submit-btn"
              >
                {createLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Organization'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Registration Cart Dialog */}
      <Dialog open={showRegistrationCart} onOpenChange={setShowRegistrationCart}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>
              <ShoppingCart className="h-5 w-5 inline mr-2" />
              Player Registration
            </DialogTitle>
          </DialogHeader>
          {organizations.length > 0 && (
            <RegistrationCart 
              orgId={organizations[0]?.id}
              onClose={() => setShowRegistrationCart(false)}
              onComplete={() => {
                setShowRegistrationCart(false);
                fetchData();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Helpdesk: any authenticated non-HQ user can file tickets that
          land in Semprion HQ's Tickets queue (/admin Tickets tab). HQ
          staff already have the full admin console, so we hide it there. */}
      {!isSemprionAdmin && <HelpdeskWidget />}
      </div>
    </div>
  );
}


/** A page's own tabs (AD Overview, AD Statistics) — same look as the hub sub-tabs. */
function PageSubTabs({ value, onChange, tabs, isDark, testid }) {
  return (
    <div className={`flex gap-1 border-b mb-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`} data-testid={testid}>
      {tabs.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${value === k
            ? 'border-amber-500 text-amber-600'
            : `border-transparent ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'}`}`}>
          {label}
        </button>
      ))}
    </div>
  );
}
